import * as O from 'fp-ts/lib/Option';

import {ExponentialBackoffOptions, withExponentialBackoff} from '../../../helpers/controlHelpers';
import {AppState} from '../../../store/appState';
import {Dispatch} from '../../../store/appStore';
import {prefetchCellsAsyncAction} from '../../../store/metadata/cellsLists/cellsListsActions';
import {findCellsListReadableForParameters} from '../../../store/metadata/cellsLists/cellsListsHelpers';
import {makeCellsContext} from '../../../store/panels/cells/context/cellsContextHelpers';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {isHttpTooManyRequestsError} from '../../http/httpError';
import {Prefetcher} from './base';
import {createOneTimePrefetcher} from './oneTime';

/*
 * Factory.
 */

export interface InitialCellsPrefetcherOptions {
  key: string;
  parameters: InboxesParameters;
  exponentialBackoffOptions: ExponentialBackoffOptions;
}

/** Creates a prefetcher that prefetches the first page of a P2. */
export function createInitialCellsPrefetcher(options: InitialCellsPrefetcherOptions): Prefetcher<void> {
  const {key, parameters, exponentialBackoffOptions} = options;

  return createOneTimePrefetcher({
    key,
    computeCondition: (state) => computeInitialCellsCondition(state, parameters),
    prefetchAsync: (dispatch) => prefetchInitialCellsAsync(dispatch, parameters, exponentialBackoffOptions),
  });
}

/*
 * Helpers.
 */

export function computeInitialCellsCondition(state: AppState, parameters: InboxesParameters): O.Option<void> {
  const cellsContext = makeCellsContext(state, parameters);

  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  const fetchRange = list && list.ranges.some((range) => !range.topBoundary);

  return fetchRange ? O.none : O.some(undefined);
}

export async function prefetchInitialCellsAsync(
  dispatch: Dispatch,
  parameters: InboxesParameters,
  exponentialBackoffOptions: ExponentialBackoffOptions,
): Promise<void> {
  return withExponentialBackoff(
    () => dispatch(prefetchCellsAsyncAction(parameters)),
    isHttpTooManyRequestsError,
    exponentialBackoffOptions,
  );
}
